import { ReactiveMap } from "@solid-primitives/map"
import { batch, createEffect, DEV, on } from "solid-js"
import type { Vector3 } from "three"
import getEntityCurrentVector3 from "../../lib/core/Current/getEntityCurrentVector3"
import eightDirections from "../../lib/core/direction/eightDirections"
import getNextEightDirection from "../../lib/core/direction/getNextEightDirection"
import type { EntityWaterWithCurrent } from "../../lib/core/entity/isEntityWaterWithCurrent"
import isEntityWaterWithCurrent from "../../lib/core/entity/isEntityWaterWithCurrent"
import { freeze } from "../../lib/core/util/Object"
import { immutableEmptyArray } from "../../lib/lib"
import createNodeNeighborAvgWaterCurrentListV1 from "../../lib/three/objects/WaterCurrent/createNodeNeighborAvgWaterCurrentListV1"
import createSharedCachedFactory from "./createSharedCachedFactory"
import { createSharedTileGraphNodeCollectionMemo } from "./createSharedTileGraphNodeCollectionMemo"
import { useNodeNeighborIndexMap } from "./useNodeNeighborIndexMap"
import { useSharedWaterEntsWithCurrentListMemo } from "./useSharedWaterEntsWithCurrentListMemo"
import { useSharedWaterFlowNodeMapMemo } from "./useSharedWaterFlowNodeMapMemo"

export type NeighborWaterCurrentCollection = {
  /** Base elevation of the center tile */
  base: Vector3
  /** Elevations of 8 neighbors in eightDirections order (length === 8) */
  list: Array<Vector3> // length === 8
  /** Pre-computed averages for interpolation (length === 8) */
  avgs: Array<Vector3> // length === 8
}

export const useNodeNeighborBaseWaterCurrentReactiveMap = createSharedCachedFactory((engine) => {
  const getNodeNeighborIndexMap = useNodeNeighborIndexMap(engine)
  const getWaterEntsWithCurrentList = useSharedWaterEntsWithCurrentListMemo(engine)
  const getTileGraphNodeList = createSharedTileGraphNodeCollectionMemo(engine)
  const waterFlowNodeMap = useSharedWaterFlowNodeMapMemo(engine)

  const map = new ReactiveMap<number, NeighborWaterCurrentCollection>()

  createEffect(() => {
    const tpNodeList = getTileGraphNodeList()

    createEffect(on(getWaterEntsWithCurrentList, (eList: Array<EntityWaterWithCurrent>, prevList: Array<EntityWaterWithCurrent> | undefined) => {
      // console.time('useNodeNeighborBaseWaterCurrentReactiveMap.effect')
      const len0 = eList.length
      prevList ||= immutableEmptyArray as unknown as Array<EntityWaterWithCurrent>
      const len1 = prevList.length
      const neighborMap = getNodeNeighborIndexMap()
      const neighborMapValues = neighborMap.values()
  
      /**
       * Creates a NeighborWaterCurrentCollection for a specific tile index.
       * 
       * Gathers elevation data for the tile and its 8 neighbors, handling missing
       * neighbors gracefully with fallback strategies. Pre-computes averaged elevations
       * for efficient interpolation during terrain mesh generation.
       * 
       * ## Neighbor Fallback Strategy:
       * 
       * For diagonal neighbors (NE, SE, SW, NW) that are missing:
       * 1. Try adjacent cardinal directions (e.g., for NE: try N, then E)
       * 2. If both adjacent directions are missing, use base elevation
       * 
       * For cardinal neighbors (N, E, S, W) that are missing:
       * - Use base elevation directly
       * 
       * @param index - Tile index to create collection for
       * @returns Complete elevation collection with base, neighbors, and averages
       */
      function make(index: number): NeighborWaterCurrentCollection | undefined {
        const wfn0 = waterFlowNodeMap.get(index)
        // const node0 = tpNodeList[index]
        // const waterEnt0 = node0?.ents().find(isEntityWaterWithCurrent)
        if (wfn0) {
          const base : Vector3 = wfn0.current
  
          // Build neighbor elevation list in eightDirections order: [N, NE, E, SE, S, SW, W, NW]
          const neighborBases = eightDirections.map((dir, dirIndex): Vector3 => {
            const neighborIndex = neighborMap.get(dir)![index]
    
            // Handle missing neighbors with fallback strategy
            if (neighborIndex === -1) {
              // For diagonal neighbors (odd indices: NE, SE, SW, NW)
              if (dirIndex % 2 === 1) {
                // Try first adjacent cardinal direction (e.g., NE -> N)
                const dir1 = getNextEightDirection(dir, 1)
                const neighborIndex1 = neighborMap.get(dir1)![index]
                if (neighborIndex1 !== -1) {
                  const wfn1 = waterFlowNodeMap.get(neighborIndex1)
                  if (wfn1) {
                    return wfn1.current
                  }
                }
    
                // Try second adjacent cardinal direction (e.g., NE -> E)
                const dir2 = getNextEightDirection(dir, -1)
                const neighborIndex2 = neighborMap.get(dir2)![index]
                if (neighborIndex2 !== -1) {
                  const wfn2 = waterFlowNodeMap.get(neighborIndex2)
                  if (wfn2) {
                    return wfn2.current
                  } 
                }
    
                // Both adjacent directions missing, use base elevation
                return base
              }
    
              // For cardinal neighbors (even indices: N, E, S, W), use base elevation
              return base
            }
    
            const neighbor = tpNodeList[neighborIndex]
            const waterEnt = neighbor?.ents().find(isEntityWaterWithCurrent)
            if (waterEnt?.current) {
              // Neighbor exists, use its elevation
              return getEntityCurrentVector3(waterEnt.current)
            }
            
            // Neighbor is not water, use base elevation
            return base
          })
    
          // Pre-compute averaged elevations for smooth interpolation
          const avgs = createNodeNeighborAvgWaterCurrentListV1(base, neighborBases)
    
          const collection : NeighborWaterCurrentCollection = {
            base,
            list: neighborBases,
            avgs,
          }
          // console.log('collection', collection)
          if (DEV) {
            freeze(collection)
          }
          
          return collection
        }
      }
  
      // Batch all map updates to minimize reactive cascades
      batch(() => {
        // Handle terrain size changes (tiles added/removed)
        if (len0 !== len1) {
          // Complete rebuild - clear map and recreate all collections
          map.clear()
          for (let i = 0; i < len0; i++) {
            const collection = make(i)
            if (collection) {
              map.set(i, collection)
            }
          }
        } else {
          // Incremental update - only update changed tiles and their neighbors
          const indexesToUpdate = new Set<number>()
  
          // Find tiles with changed elevations
          for (let i = 0; i < len0; i++) {
            const elevation0 = prevList[i]
            const elevation1 = eList[i]
            if (elevation0 !== elevation1) {
              // Mark the changed tile for update
              indexesToUpdate.add(i)
  
              // Mark all neighbors for update since they reference this tile
              for (const nList of neighborMapValues) {
                const nIndex = nList[i]
                if (nIndex !== -1) {
                  indexesToUpdate.add(nIndex)
                }
              }
            }
          }
  
          // Update only the affected tiles and their neighbors
          for (const index of indexesToUpdate) {
            const collection = make(index)
            if (collection) {
              map.set(index, collection)
            }
          }
        }
      })
      // console.timeEnd('useNodeNeighborBaseWaterCurrentReactiveMap.effect')
    }))

  })

  return map
})